import { api_admin_companies_search_list, Company, Helmet } from 'abi'
import {
    Component,
    createEffect,
    createMemo,
    For,
    on,
    onCleanup,
    Show,
} from 'solid-js'

import { HoverInp, LoadingDots, setPopup } from 'components'
import {
    CalendarIcon,
    CheckFillIcon,
    CloseIcon,
    CorpsIcon,
    WarningIcon,
} from 'icons'
import { createStore, produce } from 'solid-js/store'
import { debounce, deepcopy, objentries } from 'utils/tools'
import { FORMATS, HEL_COL_MAP, HELMET_DEF, MANUALS_LABELS } from './addIns'
import './style/select.scss'

interface SelectProps {
    count: number
    show: boolean

    pickCorp: boolean

    onClose(): void
    onSave(h: Helmet): void
}
const SelectedHelmets: Component<SelectProps> = P => {
    type STATE = {
        h: Helmet
    }

    const STATE_DEF: STATE = {
        h: deepcopy(HELMET_DEF),
    }
    const [state, setState] = createStore<STATE>(deepcopy(STATE_DEF))

    onCleanup(() => {
        setState(deepcopy(STATE_DEF))
    })

    const changed = createMemo(() => {
        type COMPARE_T = Omit<Helmet, 'id'>
        const h = state.h as Partial<COMPARE_T> | undefined
        const p = HELMET_DEF as Partial<COMPARE_T> | undefined

        if (!h && !p) return false

        if (!h || !p) return true

        const keys: (keyof Omit<Helmet, 'id'>)[] = [
            'company',
            'cargo',
            'created_at',
            'insurer',
            'v_color',
            'chassis',
            'engine',
            'plate_serial',
            'plate_number',
            'vin',
            'manufacture_year',
            'total_insurance_amount',
            'v_name',
            'v_group',
            'v_system',
            'v_usage',
            'insurance_number',
            'past_insurer',
            'reagent',
            'start_date',
            'expiration_date',
            'added_tax',
            'added_toll',
            'insurance_code',
            'issue_date',
            'issuer',
            'past_expiration_date',

            'airrm_cover_life',
            'airrm_cover_financial',
            'airrm_cover_passenger',
            'issue_time',
            'unique_code',
            'issuer_code',
            'third_party_insurance_amount',
            'passenger_insurance_amount',
            'life_surplus_insurance_amount',
            'financial_surplus_insurance_amount',
            'fund_fine',
            'print_count',

            'insurer_code',
            'issue_year',
            'economic_number',
            'provider',
            'plate_city',
            'insurer_phone',
            'passenger_count',
        ]

        if (h.company !== p.company) return true

        return keys.some(k => {
            const a = h[k]
            const b = p[k]

            if (a == null && b == null) return false

            return String(a ?? '') !== String(b ?? '')
        })
    })

    const onClose = () => {
        if (changed()) {
            setPopup({
                show: true,
                title: 'ذخیره نکردید!',
                content: 'بعد از خروج تغییرات شما ذخیره نمیشوند!',
                Icon: WarningIcon,
                type: 'warning',
                onSubmit: P.onClose,
            })
            return
        }

        P.onClose()
    }
    const onSave = () => {
        P.onSave(deepcopy(state.h))
    }

    function helmet_update(body: Partial<Helmet>) {
        setState(
            produce(s => {
                for (let [k, v] of Object.entries(body)) {
                    // @ts-ignore
                    s.h[k] = v
                }
            })
        )
    }

    const CorpPick: Component = () => {
        let resultsRef: HTMLElement

        type DATA = {
            loading: boolean
            error: boolean
            query: string
            name: string
            id: number

            corps: Company[]
        }
        const [data, setData] = createStore<DATA>({
            loading: false,
            error: false,

            id: -1,
            name: '',
            query: '',

            corps: [],
        })

        onCleanup(() => {
            setData({
                loading: false,
                error: false,

                id: -1,
                name: '',
                query: '',

                corps: [],
            })
        })

        createEffect(
            on(
                () => data.query,
                s => {
                    if (!resultsRef) return

                    setData({ loading: true, error: false })

                    if (s.length == 0) {
                        resultsRef.style.opacity = '0'
                        resultsRef.style.maxHeight = '0px'

                        return
                    }

                    searchCorps(s)
                },
                {
                    defer: true,
                }
            )
        )

        const setActive = () => {
            let height = resultsRef.scrollHeight

            resultsRef.style.maxHeight = `${height}px`
            resultsRef.style.opacity = '1'
        }

        const searchCorps = debounce(async (query: string) => {
            let res = await api_admin_companies_search_list({
                name: `%${query}%`,
            })

            setData({ loading: false })

            setActive()

            if (!res.ok() || res.body.length == 0)
                return setData({ error: true })

            if (data.query !== query) return

            setData({
                corps: res.body.slice(0, 5),
            })
        }, 200)

        const Loading = () => {
            return (
                <div class='loading-result title'>
                    <LoadingDots />
                    درحال گرفتن شرکت
                </div>
            )
        }
        const Error = () => {
            return (
                <div class='error-result title'>
                    <CloseIcon />
                    شرکتی یافت نشد!
                </div>
            )
        }

        const active = createMemo(
            () => data.name.length !== 0 || data.query.length !== 0
        )
        const value = createMemo(() => (data.name ? data.name : data.query))

        interface CorpProps extends Company {
            onClick(): void
        }
        const Corp: Component<CorpProps> = C => {
            return (
                <div role='button' class='corp-res' onclick={C.onClick}>
                    <div class='head title_small'>{C.name}</div>
                    <div class='bottom description'>
                        <div class='data'>{C.agent_count} نماینده</div>•
                        <div class='data'>
                            <CalendarIcon />
                            {new Date(C.created_at * 1e3).toLocaleDateString(
                                'fa-IR'
                            )}
                        </div>
                    </div>
                </div>
            )
        }

        return (
            <div
                class='corp-inp-rows'
                classList={{
                    active:
                        (data.loading ||
                            data.error ||
                            data.corps.length !== 0) &&
                        data.query.length !== 0,
                }}
            >
                <div class='hover-inp-wrapper'>
                    <HoverInp
                        holder={'شرکت (اختیاری)'}
                        inpType='text'
                        acitve={active()}
                        value={value()}
                        onInp={e =>
                            setData(
                                produce(s => {
                                    s.query = e
                                    s.name = ''
                                })
                            )
                        }
                        Icon={<CorpsIcon />}
                        name='corp'
                        dir='rtl'
                    />
                </div>

                <div
                    class='result-wrapper'
                    classList={{ active: data.query.length !== 0 }}
                    ref={e => (resultsRef = e)}
                >
                    <Show when={!data.loading} fallback={<Loading />}>
                        <Show when={!data.error} fallback={<Error />}>
                            <For each={data.corps}>
                                {c => (
                                    <Corp
                                        onClick={() => {
                                            setState('h', 'company', c.id)
                                            setData({
                                                name: c.name,
                                                id: c.id,
                                                query: '',
                                            })
                                        }}
                                        {...c}
                                    />
                                )}
                            </For>
                        </Show>
                    </Show>
                </div>
            </div>
        )
    }

    return (
        <div class='select-cmp-container'>
            <div class='select-ctas'>
                <button
                    class='save-select title_smaller'
                    type='button'
                    onclick={onSave}
                    disabled={!changed()}
                >
                    <CheckFillIcon />
                    تایید
                </button>

                <button
                    class='close-select icon'
                    type='button'
                    onclick={onClose}
                >
                    <CloseIcon />
                </button>
            </div>

            <div class='select-counter'>
                <span class='main section_title'>{P.count}</span>
                <span class='sub title_small'>بیمه انتخاب شده</span>
            </div>
            <div class='inputs'>
                <Show when={P.pickCorp}>
                    <CorpPick />
                </Show>

                <For each={objentries(MANUALS_LABELS)}>
                    {([k, v]) => {
                        const key = k as keyof Helmet

                        return (
                            <HoverInp
                                holder={v}
                                value={String(state.h![key] || '')}
                                onInp={e => helmet_update({ [k]: e })}
                                acitve={!!state.h?.[k]}
                                id={`inp-${String(k)}`}
                                inpType={
                                    typeof HELMET_DEF[k] == 'number'
                                        ? 'number'
                                        : 'text'
                                }
                                Icon={null}
                                format={FORMATS.includes(k)}
                                disable={k == 'provider'}
                            />
                        )
                    }}
                </For>
                <For each={objentries(HEL_COL_MAP)}>
                    {([k, { cn }]) => (
                        <HoverInp
                            holder={cn.filter(Boolean)[0]!}
                            value={String(state.h![k] || '')}
                            onInp={e => helmet_update({ [k]: e })}
                            acitve={!!state.h?.[k]}
                            id={`inp-${String(k)}`}
                            inpType={
                                typeof HELMET_DEF[k] == 'number'
                                    ? 'number'
                                    : 'text'
                            }
                            Icon={null}
                            format={FORMATS.includes(k)}
                        />
                    )}
                </For>
            </div>
        </div>
    )
}

export default SelectedHelmets
